import { useState, useMemo } from "react"
import { Users, UserPlus, Star } from "lucide-react"
import { Button } from "@/components/ui/Button"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsList, TabsTab, TabsPanel } from "@/components/ui/tabs"
import { notifyFeature } from "@/utils/demoNotifications"
import type { ViewMode, ContactFilter } from "./lib/data"
import { usePeopleData } from "@/hooks/people/usePeopleData"
import { ContactsTab } from "./components/ContactsTab"
import { MessagesTab } from "./components/MessagesTab"
import { FindHelpTab } from "./components/FindHelpTab"

const PeoplePage = () => {
  const { contacts, conversations } = usePeopleData()
  const [activeTab, setActiveTab] = useState<string | number | null>("contacts")
  const [searchQuery, setSearchQuery] = useState("")
  const [viewMode, setViewMode] = useState<ViewMode>("grid")
  const [contactFilter, setContactFilter] = useState<ContactFilter>("all")
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null)

  const filteredContacts = useMemo(() => {
    return contacts.filter((contact) => {
      const matchesSearch =
        contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contact.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contact.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
        contact.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))

      const matchesFilter =
        contactFilter === "all" ||
        (contactFilter === "starred" && contact.isStarred) ||
        contact.type === contactFilter

      return matchesSearch && matchesFilter
    })
  }, [contacts, searchQuery, contactFilter])

  const stats = {
    network: contacts.filter((c) => c.type === "network").length,
    organization: contacts.filter((c) => c.type === "organization").length,
    starred: contacts.filter((c) => c.isStarred).length,
  }

  const totalUnread = conversations
    .filter((c) => c.unread > 0)
    .reduce((sum, c) => sum + c.unread, 0)

  const handleAction = (action: string) => notifyFeature(action, true)

  return (
    <div className="h-full flex flex-col bg-background">
      <header className="shrink-0 border-b border-border bg-card/50">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center size-10 rounded-md bg-muted">
                <Users className="size-5 text-muted-foreground" />
              </div>
              <div>
                <h1 className="font-semibold text-foreground tracking-tight">People</h1>
                <p className="text-sm text-muted-foreground">Your network, team, and connections</p>
              </div>
            </div>

            <Button onClick={() => handleAction("Invite People")} className="gap-2">
              <UserPlus className="size-4" />
              <span className="hidden sm:inline">Invite</span>
            </Button>
          </div>
        </div>

        <div className="px-6 pb-4">
          <div className="flex gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="size-2 rounded-full bg-foreground/40" />
              <span className="text-muted-foreground">Network</span>
              <span className="text-foreground font-medium">{stats.network}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="size-2 rounded-full bg-foreground/30" />
              <span className="text-muted-foreground">Organization</span>
              <span className="text-foreground font-medium">{stats.organization}</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="size-4 text-muted-foreground/50" />
              <span className="text-muted-foreground">Starred</span>
              <span className="text-foreground font-medium">{stats.starred}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
          <div className="px-4 pt-2 border-b border-border">
            <TabsList variant="underline" className="gap-4">
              <TabsTab value="contacts">Contacts ({contacts.length})</TabsTab>
              <TabsTab value="messages">
                Messages{" "}
                {totalUnread > 0 && (
                  <Badge variant="error" size="sm" className="ml-2">
                    {totalUnread}
                  </Badge>
                )}
              </TabsTab>
              <TabsTab value="find">Find Help</TabsTab>
            </TabsList>
          </div>

          <TabsPanel value="contacts" className="p-0">
            <ContactsTab
              contacts={filteredContacts}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              contactFilter={contactFilter}
              onFilterChange={setContactFilter}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              onAction={handleAction}
            />
          </TabsPanel>

          <TabsPanel value="messages" className="p-6">
            <MessagesTab
              conversations={conversations}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedConversation={selectedConversation}
              onSelectConversation={setSelectedConversation}
            />
          </TabsPanel>

          <TabsPanel value="find" className="p-6">
            <FindHelpTab onAction={handleAction} />
          </TabsPanel>
        </Tabs>
      </div>
    </div>
  )
}

export default PeoplePage
